import React, { useState, useEffect, useRef } from 'react';

/**
 * Location autocomplete search input component
 * Uses Nominatim (OpenStreetMap) Search API
 *
 * @param {Object} props
 * @param {Function} props.onPlaceSelect - Callback when place is selected
 * @param {boolean} props.isArabic - RTL support
 */
const LocationAutocomplete = ({ onPlaceSelect, isArabic }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `format=json&q=${encodeURIComponent(searchQuery)}&` +
          `countrycodes=bh&` + // Restrict to Bahrain
          `addressdetails=1&limit=5&accept-language=en`,
          {
            headers: {
              'User-Agent': 'Taealam Education Platform'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setResults(data);
          setShowDropdown(data.length > 0);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPlace = (place) => {
    // Convert Nominatim result to expected format
    const formattedPlace = {
      geometry: {
        location: {
          lat: () => parseFloat(place.lat),
          lng: () => parseFloat(place.lon)
        }
      },
      formatted_address: place.display_name,
      place_id: place.place_id.toString(),
      name: place.name || place.display_name.split(',')[0]
    };

    setSearchQuery(place.display_name);
    setShowDropdown(false);
    onPlaceSelect(formattedPlace);
  };

  return (
    <div style={{
      position: 'relative',
      marginBottom: 16,
      width: '100%'
    }} ref={dropdownRef}>
      <div style={{
        position: 'absolute',
        left: isArabic ? 'auto' : 16,
        right: isArabic ? 16 : 'auto',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {isSearching ? (
          <div style={{
            width: 16,
            height: 16,
            border: '2px solid #e2e8f0',
            borderTopColor: '#FF7426',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }} />
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        )}
      </div>

      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={isArabic ? 'ابحث عن موقع...' : 'Search for a location...'}
        style={{
          width: '100%',
          padding: '14px 18px',
          paddingLeft: isArabic ? '18px' : '44px',
          paddingRight: isArabic ? '44px' : '18px',
          borderRadius: 12,
          border: '2px solid #e2e8f0',
          fontSize: 14,
          fontWeight: 500,
          outline: 'none',
          transition: 'all 0.2s',
          textAlign: isArabic ? 'right' : 'left',
          direction: isArabic ? 'rtl' : 'ltr',
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#FF7426';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,116,38,0.1)';
          if (results.length > 0) {
            setShowDropdown(true);
          }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />

      {/* Search results dropdown */}
      {showDropdown && results.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: '#fff',
          border: '2px solid #e2e8f0',
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 280,
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {results.map((place, index) => (
            <div
              key={place.place_id}
              onClick={() => handleSelectPlace(place)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < results.length - 1 ? '1px solid #f1f5f9' : 'none',
                transition: 'background 0.15s',
                textAlign: isArabic ? 'right' : 'left',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
              }}
            >
              <div style={{
                fontWeight: 600,
                fontSize: 13,
                color: '#1e293b',
                marginBottom: 2
              }}>
                {place.name || place.display_name.split(',')[0]}
              </div>
              <div style={{
                fontSize: 12,
                color: '#64748b',
                lineHeight: 1.4
              }}>
                {place.display_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LocationAutocomplete;
